import { authTokenManager } from "@legal-500/auth-client";
import * as api from "./client.js";

const INTELLIGENCE_SYNCER_BASE = "/v1/platform/intelligence-syncer";

export type IntelligenceSyncerJobType = "bulk-load" | "incremental";

export type IntelligenceSyncerProgress = {
  processed: number;
  total: number;
  skipped: number;
  errors: number;
  estimatedRemaining: string | null;
};

export type IntelligenceSyncerJobStatus =
  | { state: "queued" }
  | { state: "running"; startedAt: number; progress?: IntelligenceSyncerProgress }
  | { state: "succeeded"; durationMs: number; progress?: IntelligenceSyncerProgress }
  | { state: "failed"; error: string };

export type TriggerIntelligenceSyncerBody = {
  jobType: IntelligenceSyncerJobType;
  maxArticles?: number;
  /** Bulk-load only: true keeps the previous catalog in Redis after the new one is activated. */
  retainPreviousKeyspace?: boolean;
  /** Bulk-load only: "v2" spreads article docs across Redis shards; "v1" keeps them on one. */
  keyspaceLayout?: "v1" | "v2";
};

export type TriggerIntelligenceSyncerResponse = {
  runId: string;
  requestId: string;
};

export type IntelligenceSyncerStatusResponse = {
  runId: string;
  status: IntelligenceSyncerJobStatus;
  requestId: string;
};

export type CancelIntelligenceSyncerResponse = {
  runId: string;
  cancelled: boolean;
  reason?: string;
  requestId: string;
};

export type IntelligenceSyncerRunRawStatus =
  | "queued"
  | "running"
  | "succeeded"
  | "failed"
  | "cancelled";

export type IntelligenceSyncerRunSource = "portal" | "azure";

export type IntelligenceSyncerRunSummary = {
  runId: string;
  status: IntelligenceSyncerRunRawStatus;
  startedAt: number | null;
  finishedAt: number | null;
  durationMs: number | null;
  jobType?: IntelligenceSyncerJobType;
  executionName?: string;
  source?: IntelligenceSyncerRunSource;
  triggeredBy?: string;
};

export type ListIntelligenceSyncerRunsResponse = {
  runs: IntelligenceSyncerRunSummary[];
  requestId: string;
};

export type ResetIntelligenceSyncerStateResponse = {
  clearedRuns: number;
  requestId: string;
};

export type IntelligenceSyncerLogHandlers = {
  onLine: (line: string) => void;
  onEnd: (state: string) => void;
  onError?: (error: Error) => void;
};

export type IntelligenceSyncerLogSubscription = {
  close: () => void;
};

export type IntelligenceSyncerApiError = Error & {
  status?: number;
  code?: string;
  detailsRaw?: unknown;
};

export function isIntelligenceSyncerConflictError(
  error: unknown,
): error is IntelligenceSyncerApiError {
  return error instanceof Error && (error as IntelligenceSyncerApiError).status === 409;
}

/** Extracts the runId of the already-in-flight sync from a trigger 409's error.details. */
export function extractConflictingRunId(error: IntelligenceSyncerApiError): string | null {
  const details = error.detailsRaw;
  if (details && typeof details === "object" && "existingRunId" in details) {
    const value = (details as { existingRunId?: unknown }).existingRunId;
    return typeof value === "string" ? value : null;
  }
  return null;
}

export const intelligenceSyncerService = {
  listRuns(limit = 50): Promise<ListIntelligenceSyncerRunsResponse> {
    return api.get<ListIntelligenceSyncerRunsResponse>(
      `${INTELLIGENCE_SYNCER_BASE}/syncs?limit=${limit}`,
    );
  },

  trigger(body: TriggerIntelligenceSyncerBody): Promise<TriggerIntelligenceSyncerResponse> {
    return api.post<TriggerIntelligenceSyncerResponse>(`${INTELLIGENCE_SYNCER_BASE}/syncs`, body);
  },

  getStatus(runId: string): Promise<IntelligenceSyncerStatusResponse> {
    return api.get<IntelligenceSyncerStatusResponse>(`${INTELLIGENCE_SYNCER_BASE}/syncs/${runId}`);
  },

  cancel(runId: string): Promise<CancelIntelligenceSyncerResponse> {
    return api.del<CancelIntelligenceSyncerResponse>(`${INTELLIGENCE_SYNCER_BASE}/syncs/${runId}`);
  },

  resetState(): Promise<ResetIntelligenceSyncerStateResponse> {
    return api.post<ResetIntelligenceSyncerStateResponse>(
      `${INTELLIGENCE_SYNCER_BASE}/syncs/reset`,
    );
  },

  subscribeToLog(
    runId: string,
    handlers: IntelligenceSyncerLogHandlers,
  ): IntelligenceSyncerLogSubscription {
    const url = api.url(`${INTELLIGENCE_SYNCER_BASE}/syncs/${runId}/log`);
    const token = authTokenManager.getToken();
    const fullUrl = token ? `${url}?token=${encodeURIComponent(token)}` : url;
    const eventSource = new EventSource(fullUrl, { withCredentials: true });

    eventSource.onmessage = event => {
      handlers.onLine(event.data);
    };

    eventSource.addEventListener("end", event => {
      const message = event instanceof MessageEvent ? event.data : "completed";
      handlers.onEnd(typeof message === "string" ? message : "completed");
      eventSource.close();
    });

    eventSource.onerror = () => {
      handlers.onError?.(new Error(`SSE connection error for intelligence-syncer run ${runId}`));
    };

    return { close: () => eventSource.close() };
  },
};
